import React from 'react';
import { PostAnalytics } from '../../../types/analytics';
import { getLivePostUrl } from '../../../components/PostManager/LivePostLink';

interface PostAnalyticsTableProps {
  analyticsList: PostAnalytics[];
  loading: boolean;
  activePlatformFilter?: 'ALL' | 'FACEBOOK' | 'INSTAGRAM' | 'INSTAGRAM_REELS' | 'LINKEDIN' | 'YOUTUBE' | 'YOUTUBE_SHORTS';
}

const PostThumbnail: React.FC<{ imageUrl?: string }> = ({ imageUrl }) => {
  const [hasImageError, setHasImageError] = React.useState(false);

  if (!imageUrl || hasImageError) {
    return (
      <div className="h-10 w-10 rounded border border-theme-border bg-theme-secondary/20 flex items-center justify-center">
        <span className="material-icons text-theme-fg-muted text-lg">article</span>
      </div>
    );
  }

  return (
    <img
      src={imageUrl}
      alt=""
      className="h-10 w-10 rounded object-cover border border-theme-border transition-transform group-hover:scale-105"
      onError={() => setHasImageError(true)}
    />
  );
};

const PostAnalyticsTable: React.FC<PostAnalyticsTableProps> = ({
  analyticsList,
  loading,
  activePlatformFilter = 'ALL',
}) => {
  const normalizePlatform = (platform: string | undefined): string => {
    const token = String(platform || '')
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '_');
    if (token === 'YOUTUBE_SHORT') return 'YOUTUBE_SHORTS';
    if (token === 'YOUTUBEVIDEO') return 'YOUTUBE';
    if (token === 'INSTAGRAM_REEL') return 'INSTAGRAM_REELS';
    return token;
  };

  const prettyPlatform = (platform: string | undefined): string => {
    const normalized = normalizePlatform(platform);
    if (normalized === 'YOUTUBE_SHORTS') return 'YouTube Shorts';
    if (normalized === 'YOUTUBE') return 'YouTube';
    if (normalized === 'FACEBOOK') return 'Facebook';
    if (normalized === 'INSTAGRAM') return 'Instagram';
    if (normalized === 'INSTAGRAM_REELS') return 'Instagram Reels';
    if (normalized === 'LINKEDIN') return 'LinkedIn';
    return normalized || '-';
  };

  const hideImpressionsAndReach =
    activePlatformFilter === 'YOUTUBE' || activePlatformFilter === 'YOUTUBE_SHORTS';
  const hideVideoViews =
    activePlatformFilter === 'FACEBOOK' || activePlatformFilter === 'LINKEDIN' || activePlatformFilter === 'INSTAGRAM';
  const engagementLabel = hideImpressionsAndReach ? 'Interactions' : 'Engagement';

  if (loading) {
    const skeletonCols =
      3 + // Post, Platform, Engagement
      (hideImpressionsAndReach ? 0 : 2) + // Impressions, Reach
      3 + // Likes, Comments, Shares
      (hideVideoViews ? 0 : 1); // Video Views

    return (
      <div className="overflow-hidden rounded-lg border border-theme-border bg-theme-surface shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm text-theme-fg">
            <thead className="border-b border-theme-border bg-theme-background text-xs uppercase text-theme-fg-muted">
              <tr>
                {[...Array(skeletonCols)].map((_, i) => (
                  <th key={i} className="px-6 py-4">
                    <div className="h-3 rounded bg-theme-border/50 animate-pulse" style={{ width: i === 0 ? '60%' : '40%' }}></div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-theme-border">
              {[...Array(6)].map((_, rowIdx) => (
                <tr key={rowIdx} className="animate-pulse">
                  {/* Post column — thumbnail + text */}
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="h-10 w-10 flex-shrink-0 rounded bg-theme-border/40"></div>
                      <div className="h-4 w-36 rounded bg-theme-border/40"></div>
                    </div>
                  </td>
                  {/* Remaining columns */}
                  {[...Array(skeletonCols - 1)].map((_, colIdx) => (
                    <td key={colIdx} className="px-6 py-4 text-right">
                      <div className="ml-auto h-4 w-10 rounded bg-theme-border/40"></div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  if (!analyticsList || !Array.isArray(analyticsList) || analyticsList.length === 0) {
    return (
      <div className="rounded-lg border border-theme-border bg-theme-surface p-8 text-center text-theme-fg-muted shadow-sm">
        <span className="material-icons mb-2 text-4xl text-theme-border">
          bar_chart
        </span>
        <p>No post analytics available for this customer.</p>
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-lg border border-theme-border bg-theme-surface shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm text-theme-fg">
          <thead className="border-b border-theme-border bg-theme-background text-xs uppercase text-theme-fg-muted">
            <tr>
              <th className="px-6 py-4 font-semibold cursor-help" title="The post's title, image, and link to the live post on the platform.">Post</th>
              <th className="px-6 py-4 font-semibold cursor-help" title="The social media platform where the post was published.">Platform</th>
              {!hideImpressionsAndReach && (
                <th className="px-6 py-4 font-semibold text-right cursor-help" title="The total number of times your content was displayed on screen.">Impressions</th>
              )}
              {!hideImpressionsAndReach && (
                <th className="px-6 py-4 font-semibold text-right cursor-help" title="The number of unique users who saw your content.">Reach</th>
              )}
              <th className="px-6 py-4 font-semibold text-right cursor-help" title="The total number of interactions (likes, comments, shares, etc.) with your content.">{engagementLabel}</th>

              <th className="px-6 py-4 font-semibold text-right cursor-help" title="The total number of likes received.">Likes</th>
              <th className="px-6 py-4 font-semibold text-right cursor-help" title="The total number of comments received.">Comments</th>
              <th className="px-6 py-4 font-semibold text-right cursor-help" title="The total number of times your content was shared.">Shares</th>
              {!hideVideoViews && (
                <th className="px-6 py-4 font-semibold text-right cursor-help" title="The total number of times your videos were viewed.">Video Views</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-theme-border">
            {analyticsList.map((item, index) => {
              const normalizedPlatform = normalizePlatform(item.platform);
              const liveUrl = getLivePostUrl(
                normalizedPlatform,
                item.platform_post_id,
                item.postReference?.business_profile_id,
                item.postReference?.platform_response
              );

              return (
                <tr
                  key={`${item.post_id}-${index}`}
                  className="transition-colors hover:bg-theme-secondary/5"
                >
                  <td className="px-6 py-4">
                    {liveUrl ? (
                      <a
                        href={liveUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-3 group"
                        title="View original post"
                      >
                        <div className="relative flex-shrink-0">
                          <PostThumbnail imageUrl={item.postReference?.image_url} />
                          <div className="absolute -right-1 -top-1 hidden h-4 w-4 items-center justify-center rounded-full bg-brand-primary text-[10px] text-white shadow-sm group-hover:flex">
                            <span className="material-icons text-[10px]">open_in_new</span>
                          </div>
                        </div>
                        <div className="min-w-0 max-w-[200px]">
                          <p className="truncate font-medium text-theme-fg group-hover:text-brand-primary transition-colors" title={item.postReference?.title || item.postReference?.content || 'No title'}>
                            {item.postReference?.title || item.postReference?.content || 'No title'}
                          </p>
                        </div>
                      </a>
                    ) : (
                      <div className="flex items-center gap-3">
                        <div className="flex-shrink-0">
                          <PostThumbnail imageUrl={item.postReference?.image_url} />
                        </div>
                        <div className="min-w-0 max-w-[200px]">
                          <p className="truncate font-medium text-theme-fg" title={item.postReference?.title || item.postReference?.content || 'No title'}>
                            {item.postReference?.title || item.postReference?.content || 'No title'}
                          </p>
                        </div>
                      </div>
                    )}
                  </td>
                <td className="px-6 py-4 font-medium whitespace-nowrap">
                  <div className="flex items-center gap-2">
                    {normalizedPlatform === 'FACEBOOK' ? (
                      <span className="material-icons text-blue-600">facebook</span>
                    ) : normalizedPlatform === 'INSTAGRAM' || normalizedPlatform === 'INSTAGRAM_REELS' ? (
                      <span className="material-icons text-pink-600 text-sm">camera_alt</span>
                    ) : normalizedPlatform === 'YOUTUBE' || normalizedPlatform === 'YOUTUBE_SHORTS' ? (
                      <span className="material-icons text-red-600">smart_display</span>
                    ) : normalizedPlatform === 'LINKEDIN' ? (
                      <span className="material-icons text-[#0A66C2] text-sm">work</span>
                    ) : (
                      <span className="material-icons text-gray-400">public</span>
                    )}
                    {prettyPlatform(item.platform)}
                  </div>
                </td>
                {!hideImpressionsAndReach && (
                  <td className="px-6 py-4 text-right">
                    {item.impressions?.toLocaleString() ?? '-'}
                  </td>
                )}
                {!hideImpressionsAndReach && (
                  <td className="px-6 py-4 text-right">
                    {item.reach?.toLocaleString() ?? '-'}
                  </td>
                )}
                <td className="px-6 py-4 text-right">
                  {item.engagement?.toLocaleString() ?? '-'}
                </td>

                <td className="px-6 py-4 text-right">
                  {item.likes?.toLocaleString() ?? '-'}
                </td>
                <td className="px-6 py-4 text-right">
                  {item.comments?.toLocaleString() ?? '-'}
                </td>
                <td className="px-6 py-4 text-right">
                  {item.shares?.toLocaleString() ?? '-'}
                </td>
                {!hideVideoViews && (
                  <td className="px-6 py-4 text-right">
                    {normalizedPlatform === 'INSTAGRAM' || normalizedPlatform === 'FACEBOOK' ? '-' : (item.video_views?.toLocaleString() ?? '-')}
                  </td>
                )}
              </tr>
            );
          })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PostAnalyticsTable;
